import { authFetch } from '../util/api';
import {
  Assignment,
  AssignmentActivity,
  AssignmentStatus,
  FileLabel,
  TimeEntry,
  calculateTotalTimeSpent,
  createTimeEntry,
  getAssignmentStatusLabel,
} from './assignment';

// API functions for assignments
export async function getFileAssignments(
  fileId: string,
  workspaceId: string,
): Promise<Assignment[]> {
  try {
    return await authFetch(`/assignments?fileId=${fileId}&workspaceId=${workspaceId}`);
  } catch (error) {
    console.error('Error fetching assignments:', error);
    throw error;
  }
}

export async function getUserAssignments(
  userId: string,
  workspaceId: string,
): Promise<Assignment[]> {
  try {
    return await authFetch(`/assignments?userId=${userId}&workspaceId=${workspaceId}`);
  } catch (error) {
    console.error('Error fetching user assignments:', error);
    throw error;
  }
}

export async function createNewAssignment(
  assignment: Pick<
    Assignment,
    | 'fileId'
    | 'workspaceId'
    | 'title'
    | 'type'
    | 'createdBy'
    | 'assignees'
    | 'priority'
    | 'description'
    | 'dueDate'
    | 'labels'
  > & { userName: string },
): Promise<Assignment> {
  try {
    return await authFetch(`/assignments`, { method: 'POST', body: JSON.stringify(assignment) });
  } catch (error) {
    console.error('Error creating assignment:', error);
    throw error;
  }
}

export async function updateAssignment(
  assignmentId: string,
  workspaceId: string,
  updates: Partial<Assignment>,
  userId: string,
  userName: string,
  action: string,
): Promise<Assignment> {
  try {
    return await authFetch(`/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...updates,
        workspaceId,
        assignmentId,
        // Include metadata about who made the change and what action was taken
        updateMetadata: {
          userId,
          userName,
          action,
        },
      }),
    });
  } catch (error) {
    console.error('Error updating assignment:', error);
    throw error;
  }
}

export async function deleteAssignment(assignmentId: string, workspaceId: string): Promise<void> {
  try {
    await authFetch(`/assignments/${assignmentId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        workspaceId,
      }),
    });
  } catch (error) {
    console.error('Error deleting assignment:', error);
    throw error;
  }
}

export async function updateAssignmentStatus(
  assignmentId: string,
  workspaceId: string,
  status: AssignmentStatus,
  userId: string,
  userName: string,
): Promise<Assignment> {
  try {
    let updates: Partial<Assignment> = { status };

    // If marking as completed and there's an active time entry, stop it
    if (status === 'completed') {
      try {
        // Get the assignment to find active time entry
        const assignment = await getAssignmentById(assignmentId, workspaceId);

        // Find active time entry for this user
        const activeTimeEntry = assignment.timeEntries?.find(
          (entry) => entry.userId === userId && entry.startTime && !entry.endTime,
        );

        if (activeTimeEntry) {
          // Stop the active timer for this user on this assignment
          await stopTimeTracking(assignmentId, workspaceId, userId, userName, activeTimeEntry.id);
        }
      } catch (error) {
        console.log('No active timer to stop or error stopping timer:', error);
        // Continue with status update even if stopping timer fails
      }

      // Add completedAt and completedBy fields
      updates = {
        ...updates,
        completedAt: Date.now(),
        completedBy: userId,
      };
    }

    // Update the assignment status
    const updatedAssignment = await updateAssignment(
      assignmentId,
      workspaceId,
      updates,
      userId,
      userName,
      `marked as ${getAssignmentStatusLabel(status).toLowerCase()}`,
    );

    return updatedAssignment;
  } catch (error) {
    console.error('Error updating assignment status:', error);
    throw error;
  }
}

export async function addAssignmentAssignee(
  assignmentId: string,
  workspaceId: string,
  assigneeId: string,
  userId: string,
  userName: string,
  assigneeName: string,
): Promise<Assignment> {
  try {
    // Get the current assignment data
    const assignment = await authFetch(`/assignments/${assignmentId}?workspaceId=${workspaceId}`);

    // Don't add duplicate assignees
    if (assignment.assignees.includes(assigneeId)) {
      return assignment;
    }

    const updatedAssignees = [...assignment.assignees, assigneeId];

    return await updateAssignment(
      assignmentId,
      workspaceId,
      { assignees: updatedAssignees },
      userId,
      userName,
      `added assignee ${assigneeName}`,
    );
  } catch (error) {
    console.error('Error adding assignee:', error);
    throw error;
  }
}

export async function removeAssignmentAssignee(
  assignmentId: string,
  workspaceId: string,
  assigneeId: string,
  userId: string,
  userName: string,
  assigneeName: string,
): Promise<Assignment> {
  try {
    // Get the current assignment data
    const assignment = await authFetch(`/assignments/${assignmentId}?workspaceId=${workspaceId}`);

    const updatedAssignees = assignment.assignees.filter((id: string) => id !== assigneeId);

    return await updateAssignment(
      assignmentId,
      workspaceId,
      { assignees: updatedAssignees },
      userId,
      userName,
      `removed assignee ${assigneeName}`,
    );
  } catch (error) {
    console.error('Error removing assignee:', error);
    throw error;
  }
}

// File label functions
export async function getFileLabels(fileId: string, workspaceId: string): Promise<FileLabel[]> {
  try {
    return await authFetch(`/labels?fileId=${fileId}&workspaceId=${workspaceId}`);
  } catch (error) {
    console.error('Error fetching file labels:', error);
    throw error;
  }
}

export async function createFileLabel(
  fileId: string,
  workspaceId: string,
  name: string,
  color: string,
  createdBy: string,
): Promise<FileLabel> {
  try {
    return await authFetch(`/labels`, {
      method: 'POST',
      body: JSON.stringify({
        fileId,
        workspaceId,
        name,
        color,
        createdBy,
      }),
    });
  } catch (error) {
    console.error('Error creating file label:', error);
    throw error;
  }
}

export async function deleteFileLabel(labelId: string, workspaceId: string): Promise<void> {
  try {
    await authFetch(`/labels/${labelId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        workspaceId,
      }),
    });
  } catch (error) {
    console.error('Error deleting file label:', error);
    throw error;
  }
}

// Time tracking functions
export async function startTimeTracking(
  assignmentId: string,
  workspaceId: string,
  userId: string,
  userName: string,
): Promise<Assignment> {
  try {
    // Create a new time entry starting now
    const now = Date.now();
    // Add this time entry to the database
    const response = await authFetch(
      `/assignments/${assignmentId}/assignees/${userId}/timeEntries`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
          assignmentId,
          assigneeId: userId,
          isManualEntry: false,
          startTime: now,
          userName,
          action: 'started_timer',
        }),
      },
    );

    if (response.error) {
      throw new Error(response.error);
    }

    // Make sure the assignment status is updated to in_progress if it was pending
    // This is done automatically on the server side

    return response;
  } catch (error) {
    console.error('Error starting time tracking:', error);
    throw error;
  }
}

export async function stopTimeTracking(
  assignmentId: string,
  workspaceId: string,
  userId: string,
  userName: string,
  timeEntryId: string,
): Promise<Assignment> {
  try {
    // The backend will find the active time entry and add an end time to it
    const now = Date.now();
    // Add this time entry to the database
    const response = await authFetch(
      `/assignments/${assignmentId}/assignees/${userId}/timeEntries/${timeEntryId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
          assignmentId,
          assigneeId: userId,
          id: timeEntryId,
          endTime: now,
          userName,
          action: 'stopped_timer',
        }),
      },
    );

    if (response.error) {
      throw new Error(response.error);
    }

    return response;
  } catch (error) {
    console.error('Error stopping time tracking:', error);
    throw error;
  }
}

export async function addManualTimeEntry(
  assignmentId: string,
  workspaceId: string,
  userId: string,
  userName: string,
  startTime: number,
  endTime: number,
  notes?: string,
): Promise<Assignment> {
  try {
    if (startTime >= endTime) {
      throw new Error('End time must be after start time');
    }

    const assignment = await getAssignmentById(assignmentId, workspaceId);
    const duration = endTime - startTime;

    const timeEntry = createTimeEntry(
      assignmentId,
      userId,
      userName,
      startTime,
      endTime,
      duration,
      notes,
      true, // isManualEntry = true
    );

    // Add manual entry to time entries array
    const timeEntries = [...(assignment.timeEntries || []), timeEntry];

    // Calculate total time spent using the shared function
    const totalTimeSpent = calculateTotalTimeSpent(timeEntries);

    // Update assignment with new time entry and total time
    return await updateAssignment(
      assignmentId,
      workspaceId,
      {
        timeEntries,
        totalTimeSpent,
      },
      userId,
      userName,
      'added manual time entry',
    );
  } catch (error) {
    console.error('Error adding manual time entry:', error);
    throw error;
  }
}

export async function editTimeEntry(
  assignmentId: string,
  workspaceId: string,
  entryId: string,
  userId: string,
  userName: string,
  updates: Partial<TimeEntry>,
): Promise<Assignment> {
  try {
    const assignment = await getAssignmentById(assignmentId, workspaceId);
    if (!assignment || !assignment.timeEntries) {
      throw new Error('Assignment or time entries not found');
    }

    // Find the time entry to edit
    const timeEntryIndex = assignment.timeEntries.findIndex((entry) => entry.id === entryId);
    if (timeEntryIndex === -1) {
      throw new Error('Time entry not found');
    }

    // Update the entry
    const oldEntry = assignment.timeEntries[timeEntryIndex];
    const updatedEntry = { ...oldEntry, ...updates };

    // Calculate the duration if start and end times are provided
    if (updatedEntry.startTime && updatedEntry.endTime) {
      updatedEntry.duration = updatedEntry.endTime - updatedEntry.startTime;
    }

    // Update the time entries array
    const timeEntries = [...assignment.timeEntries];
    timeEntries[timeEntryIndex] = updatedEntry;

    // Calculate total time spent using the shared function
    const totalTimeSpent = calculateTotalTimeSpent(timeEntries);

    // Update assignment with edited time entry
    return await updateAssignment(
      assignmentId,
      workspaceId,
      {
        timeEntries,
        totalTimeSpent,
      },
      userId,
      userName,
      'edited time entry',
    );
  } catch (error) {
    console.error('Error editing time entry:', error);
    throw error;
  }
}

export async function deleteTimeEntry(
  assignmentId: string,
  workspaceId: string,
  entryId: string,
  userId: string,
  userName: string,
): Promise<Assignment> {
  try {
    // Get the current assignment
    const assignment = await getAssignmentById(assignmentId, workspaceId);
    if (!assignment || !assignment.timeEntries) {
      throw new Error('Assignment or time entries not found');
    }

    // Find the time entry to delete
    const entryToDelete = assignment.timeEntries.find((entry) => entry.id === entryId);
    if (!entryToDelete) {
      throw new Error('Time entry not found');
    }

    // Make a deep copy of the current time entries array
    const currentTimeEntries = JSON.parse(
      JSON.stringify(assignment.timeEntries || []),
    ) as TimeEntry[];

    // Remove the entry from time entries array
    const timeEntries = currentTimeEntries.filter((entry: TimeEntry) => entry.id !== entryId);

    // Calculate total time spent using the shared function
    const totalTimeSpent = calculateTotalTimeSpent(timeEntries);

    console.log('Deleting time entry:', entryId);
    console.log('Updated time entries:', timeEntries);

    // Update assignment with removed time entry
    return await updateAssignment(
      assignmentId,
      workspaceId,
      {
        timeEntries,
        totalTimeSpent,
      },
      userId,
      userName,
      'deleted time entry',
    );
  } catch (error) {
    console.error('Error deleting time entry:', error);
    throw error;
  }
}

// Helper function to get a single assignment by ID
export async function getAssignmentById(
  assignmentId: string,
  workspaceId: string,
): Promise<Assignment> {
  try {
    return await authFetch(`/assignments/${assignmentId}?workspaceId=${workspaceId}`);
  } catch (error) {
    console.error('Error fetching assignment:', error);
    throw error;
  }
}

// Assignment activity functions
export async function getAssignmentActivities(assignmentId: string): Promise<AssignmentActivity[]> {
  try {
    return await authFetch(`/assignments/${assignmentId}/activities`);
  } catch (error) {
    console.error('Error fetching assignment activities:', error);
    throw error;
  }
}
